import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import useModals from '../../../../hooks/useModals'
import {DELETE, GET} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import {ModalDelete} from '../../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../../components/Datatable/SwitchStatus'

const GradesHotelBookingContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const {switchStatus} = useSwitchStatus()
  /**
   * Tipos locais para essa página
   */
  type GridBookingConfigItem = {
    name: string
    value: number
  }

  type GridBookingApiItem = {
    id: number
    pluginspaceId: number
    title: string
    content: {
      config: GridBookingConfigItem[]
    }
    created: string
    updated: string | null
    status: boolean
    description?: string
    capacity?: number
    multiple?: boolean
  }

  type GridBookingRow = {
    id: number
    title: string
    status: boolean
    capacity: number
    multiple: string
  }

  const [grids, setGrids] = useState<GridBookingRow[]>([])
  const [id, setId] = useState<number | null>(null)
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)

  const getGrids = async () => {
    try {
      setLoading(true)
      const res = await GET(`/grid-hotel-booking`)
      if (res && res.data) {
        const apiData = res.data as GridBookingApiItem[]
        const news = apiData.map((row) => ({
          id: row.id,
          title: row.title,
          status: row.status,
          capacity: row.capacity ?? 0,
          multiple: row.multiple
            ? (i18n.t(`booking_hotel_services.grid_multiple_yes`) as string)
            : (i18n.t(`booking_hotel_services.grid_multiple_no`) as string),
        }))
        setGrids(news)
        setMeta(res.meta)
      }
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }

  const deleteGrid = async () => {
    try {
      setLoading(true)
      const gridRes = await DELETE(`/grid-hotel-booking/${id}`)
      setLoading(false)
      if (gridRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: i18n.t(`gridHotelFeedback.grid_deleted`) as string,
          text: i18n.t(`gridHotelFeedback.grid_deleted_message`) as string,
        }).then(async () => await getGrids())
      } else {
        Swal.fire({
          icon: 'error',
          title: i18n.t(`gridHotelFeedback.error`) as string,
          text: gridRes.message || (i18n.t(`gridHotelFeedback.error_delete_message`) as string),
        }).then(async () => await getGrids())
      }
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao deletar',
        text: 'Ocorreu um erro ao deletar a grade. Tente novamente mais tarde.',
      }).then(async () => await getGrids())
    }
  }

  useEffect(() => {
    getGrids()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t(`gridHotelFeedback.service_grid`)}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t(`gridHotelFeedback.listed_grids`)}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`gridHotelFeedback.create_service_grid`) as string}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/context/grid-hotel-booking/create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={grids}
                headers={[
                  {
                    key: i18n.t(`gridHotelFeedback.title`) as string,
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: i18n.t(`booking_hotel_services.grid_capacity`) as string,
                    value: 'capacity',
                    type: 'default',
                  },
                  {
                    key: i18n.t(`booking_hotel_services.grid_multiple`) as string,
                    value: 'multiple',
                    type: 'default',
                  },
                  {
                    key: i18n.t(`gridHotelFeedback.status`) as string,
                    value: 'status',
                    type: 'check',
                    action: (item: any) =>
                      switchStatus({
                        endpoint: `grid-hotel-booking`,
                        Id: item.id,
                        status: item.status,
                      }),
                  },
                  {
                    key: i18n.t(`gridHotelFeedback.general.actions`) as string,
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) =>
                      navigate(`/hospitality/context/grid-hotel-booking/edit/${item.id}`),
                    title: i18n.t(`gridHotelFeedback.edit_service_grid`) as string,
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t(`gridHotelFeedback.grid_deleted`) as string)
                      setId(item.id)
                    },
                    title: i18n.t(`gridHotelFeedback.grid_deleted`) as string,
                  },
                ]}
                color='primary'
                meta={meta}
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteGrid()} />
        </div>
      )}
    </>
  )
}

const GradesBooking: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t(`gridHotelFeedback.dashboard`) as string,
            path: 'dashboard',
            isActive: true,
          },
          {title: i18n.t(`gridHotelFeedback.service_grid`) as string, path: 'grid', isActive: true},
        ]}
      >
        {i18n.t(`gridHotelFeedback.service_grid`)}
      </PageTitle>
      <GradesHotelBookingContent />
    </>
  )
}

export {GradesBooking}
